import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  Grid, MenuItem, Slider, TextField, Typography,
} from '@mui/material';
import L from 'leaflet';
import { Circle, MapContainer, Marker, TileLayer, useMap, useMapEvents } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';

const INDIA_CENTER = [20.5937, 78.9629];
const GEOCODING_URL = process.env.REACT_APP_GEOCODING_URL || 'https://nominatim.openstreetmap.org/search';
const STATES = [
  'Andaman and Nicobar Islands', 'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar',
  'Chandigarh', 'Chhattisgarh', 'Dadra and Nagar Haveli and Daman and Diu', 'Delhi', 'Goa',
  'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jammu and Kashmir', 'Jharkhand', 'Karnataka',
  'Kerala', 'Ladakh', 'Lakshadweep', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya',
  'Mizoram', 'Nagaland', 'Odisha', 'Puducherry', 'Punjab', 'Rajasthan', 'Sikkim',
  'Tamil Nadu', 'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
];

const pinIcon = L.divIcon({
  className: '',
  html: '<div style="width:22px;height:22px;border-radius:50% 50% 50% 0;background:#2f6bff;border:3px solid white;box-shadow:0 2px 8px #334155;transform:rotate(-45deg)"></div>',
  iconSize: [22, 22], iconAnchor: [11, 22],
});

function MapController({ position, onChange }) {
  const map = useMap();
  useEffect(() => {
    if (position) map.setView(position, Math.max(map.getZoom(), 15));
  }, [map, position]);
  useMapEvents({ click: (event) => onChange(event.latlng) });
  return null;
}

const blank = {
  name: '', address: '', city: '', state: '', postal_code: '',
  latitude: '', longitude: '', geofence_radius_meters: 100,
};

const coordinateMatches = (value) => [...String(value || '')
  .replace(/[−–—]/g, '-')
  .matchAll(/(-?\d+(?:\.\d+)?)\s*°?\s*([NSEW])?/gi)];

const coordinateFromMatch = (match, axis) => {
  if (!match) return null;
  const hemisphere = String(match[2] || '').toUpperCase();
  if (axis === 'latitude' && hemisphere && !['N', 'S'].includes(hemisphere)) return null;
  if (axis === 'longitude' && hemisphere && !['E', 'W'].includes(hemisphere)) return null;
  const magnitude = Number(match[1]);
  if (!Number.isFinite(magnitude)) return null;
  const value = ['S', 'W'].includes(hemisphere) ? -Math.abs(magnitude) : magnitude;
  const limit = axis === 'latitude' ? 90 : 180;
  return Math.abs(value) <= limit ? value : null;
};

const parseCoordinate = (value, axis) => coordinateFromMatch(coordinateMatches(value)[0], axis);

const parseCoordinatePair = (value) => {
  const matches = coordinateMatches(value);
  if (matches.length < 2) return null;
  const latitudeMatch = matches.find((match) => ['N', 'S'].includes(String(match[2] || '').toUpperCase())) || matches[0];
  const longitudeMatch = matches.find((match) => ['E', 'W'].includes(String(match[2] || '').toUpperCase())) || matches[1];
  const latitude = coordinateFromMatch(latitudeMatch, 'latitude');
  const longitude = coordinateFromMatch(longitudeMatch, 'longitude');
  return latitude === null || longitude === null ? null : { latitude, longitude };
};

const displayCoordinate = (value) => Number(value).toFixed(6);

export default function BranchDialog({ open, item, loading, onClose, onSave }) {
  const [form, setForm] = useState(blank);
  const [errors, setErrors] = useState({});
  const [geocoding, setGeocoding] = useState({ status: 'idle', message: '' });
  const markerRef = useRef(null);
  const geocodingRequestRef = useRef(null);

  useEffect(() => {
    setForm(item ? {
      ...blank, ...item,
      latitude: item.latitude ?? '', longitude: item.longitude ?? '',
    } : blank);
    setErrors({});
    setGeocoding({ status: 'idle', message: '' });
    geocodingRequestRef.current?.abort();
  }, [item, open]);

  useEffect(() => () => geocodingRequestRef.current?.abort(), []);

  const position = useMemo(() => {
    const lat = parseCoordinate(form.latitude, 'latitude');
    const lng = parseCoordinate(form.longitude, 'longitude');
    return lat !== null && lng !== null ? [lat, lng] : null;
  }, [form.latitude, form.longitude]);

  const setPosition = ({ lat, lng }) => setForm((current) => ({
    ...current, latitude: lat.toFixed(6), longitude: lng.toFixed(6),
  }));

  const findAddressLocation = async () => {
    const address = form.address.trim();
    if (address.length < 5) return;

    geocodingRequestRef.current?.abort();
    const controller = new AbortController();
    geocodingRequestRef.current = controller;
    setGeocoding({ status: 'loading', message: 'Finding the address location…' });

    const simplifiedAddress = address
      .replace(/\([^)]*\)/g, ' ')
      .replace(/\barea\b/gi, ' ')
      .replace(/\s+/g, ' ')
      .replace(/\s+,/g, ',')
      .trim();
    const locality = [form.postal_code, form.city, form.state, 'India']
      .map((value) => String(value || '').trim())
      .filter(Boolean)
      .join(', ');
    const queries = [...new Set([address, simplifiedAddress, locality].filter((query) => query.length >= 5))];

    try {
      let result = null;
      for (const query of queries) {
        const params = new URLSearchParams({
          q: query,
          format: 'jsonv2',
          addressdetails: '1',
          countrycodes: 'in',
          limit: '1',
        });
        const response = await fetch(`${GEOCODING_URL}?${params.toString()}`, {
          headers: { Accept: 'application/json' },
          signal: controller.signal,
        });
        if (!response.ok) throw new Error('Address lookup failed.');
        const results = await response.json();
        result = results?.[0] || null;
        if (result) break;
      }
      const lat = Number(result?.lat);
      const lng = Number(result?.lon);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
        setGeocoding({
          status: 'error',
          message: 'Location not found. Refine the address or choose it on the map.',
        });
        return;
      }

      const details = result.address || {};
      const resolvedCity = details.city || details.town || details.village || details.municipality || '';
      const resolvedState = STATES.find((state) => state.toLowerCase() === String(details.state || '').toLowerCase()) || '';
      setForm((current) => ({
        ...current,
        latitude: lat.toFixed(6),
        longitude: lng.toFixed(6),
        city: current.city || resolvedCity,
        state: current.state || resolvedState,
        postal_code: current.postal_code || details.postcode || '',
      }));
      setErrors((current) => ({ ...current, coordinates: undefined }));
      setGeocoding({ status: 'success', message: 'Location found and pinned on the map.' });
    } catch (error) {
      if (error.name !== 'AbortError') {
        setGeocoding({
          status: 'error',
          message: 'Could not look up this address. Check your connection or choose it on the map.',
        });
      }
    }
  };

  const submit = () => {
    const nextErrors = {};
    if (!form.name.trim()) nextErrors.name = 'Branch name is required.';
    if (!form.address.trim()) nextErrors.address = 'Address is required.';
    if (!form.state) nextErrors.state = 'State is required.';
    if (!position) {
      const hasOneCoordinate = String(form.latitude || '').trim() || String(form.longitude || '').trim();
      nextErrors.coordinates = hasOneCoordinate
        ? 'Enter both latitude and longitude, paste the complete Google Maps coordinate pair, or choose the point on the map.'
        : 'Choose a location on the map.';
    }
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;
    onSave({
      ...form,
      name: form.name.trim(), address: form.address.trim(),
      latitude: position[0], longitude: position[1],
      geofence_radius_meters: Number(form.geofence_radius_meters),
    });
  };

  const changeCoordinate = (axis) => (event) => {
    const value = event.target.value;
    const pair = parseCoordinatePair(value);
    if (pair) {
      setForm((current) => ({
        ...current,
        latitude: displayCoordinate(pair.latitude),
        longitude: displayCoordinate(pair.longitude),
      }));
      setErrors((current) => ({ ...current, coordinates: undefined }));
      setGeocoding({ status: 'success', message: 'Coordinates pasted and pinned on the map.' });
      return;
    }
    setForm((current) => ({ ...current, [axis]: value }));
  };

  const normalizeCoordinate = (axis) => () => {
    const parsed = parseCoordinate(form[axis], axis);
    if (parsed !== null) {
      setForm((current) => ({ ...current, [axis]: displayCoordinate(parsed) }));
    }
  };

  const field = (name) => ({
    value: form[name],
    onChange: (event) => setForm({ ...form, [name]: event.target.value }),
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      sx={{ zIndex: (theme) => theme.zIndex.modal + 2 }}
      PaperProps={{
        sx: {
          borderRadius: '22px',
          maxHeight: 'calc(100dvh - 32px)',
          m: { xs: 1, sm: 2 },
          overflow: 'hidden',
          display: 'flex',
        },
      }}
    >
      <DialogTitle sx={{ fontWeight: 800, flexShrink: 0, bgcolor: '#fff', pb: 1.5 }}>
        {item ? 'Update Branch' : 'Register New Branch'}
      </DialogTitle>
      <DialogContent dividers sx={{ overflowY: 'auto', px: { xs: 2, sm: 3 }, py: 2 }}>
        <Grid container spacing={2} sx={{ mt: 0.25 }}>
          <Grid item xs={12} sm={6}><TextField label="Branch Name" fullWidth {...field('name')} error={!!errors.name} helperText={errors.name} /></Grid>
          <Grid item xs={12} sm={6}><TextField label="City" fullWidth {...field('city')} /></Grid>
          <Grid item xs={12}>
            <TextField
              label="Address"
              fullWidth
              multiline
              minRows={2}
              value={form.address}
              onChange={(event) => {
                setForm({ ...form, address: event.target.value, latitude: '', longitude: '' });
                setGeocoding({ status: 'idle', message: '' });
              }}
              onBlur={findAddressLocation}
              error={!!errors.address}
              helperText={errors.address || geocoding.message}
              FormHelperTextProps={{
                sx: { color: geocoding.status === 'success' ? 'success.main' : geocoding.status === 'error' ? 'error.main' : undefined },
              }}
              InputProps={{
                endAdornment: geocoding.status === 'loading' ? <CircularProgress size={20} /> : null,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              select
              label="State / Union Territory"
              fullWidth
              {...field('state')}
              error={!!errors.state}
              helperText={errors.state}
              SelectProps={{ MenuProps: { sx: { zIndex: (theme) => theme.zIndex.modal + 3 } } }}
            >
              {STATES.map((state) => <MenuItem key={state} value={state}>{state}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}><TextField label="Postal Code" fullWidth {...field('postal_code')} /></Grid>
          <Grid item xs={12} sm={6}><TextField label="Latitude" fullWidth value={form.latitude} onChange={changeCoordinate('latitude')} onBlur={normalizeCoordinate('latitude')} InputLabelProps={{ shrink: true }} inputProps={{ inputMode: 'decimal' }} /></Grid>
          <Grid item xs={12} sm={6}><TextField label="Longitude" fullWidth value={form.longitude} onChange={changeCoordinate('longitude')} onBlur={normalizeCoordinate('longitude')} InputLabelProps={{ shrink: true }} inputProps={{ inputMode: 'decimal' }} /></Grid>
          <Grid item xs={12}>
            <Typography fontWeight={700}>Geofence radius: {form.geofence_radius_meters} meters</Typography>
            <Slider aria-label="Geofence radius" min={20} max={1000} step={10} value={Number(form.geofence_radius_meters)} onChange={(_, value) => setForm({ ...form, geofence_radius_meters: value })} />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body2" color={errors.coordinates ? 'error' : 'text.secondary'} sx={{ mb: 1 }}>
              {errors.coordinates || 'Click the map or drag the pin to set the branch location.'}
            </Typography>
            <Box
              data-testid="branch-map"
              sx={{
                height: { xs: 240, sm: 'clamp(240px, 34vh, 340px)' },
                minHeight: 240,
                borderRadius: 2,
                overflow: 'hidden',
                border: '1px solid #dbe2ea',
              }}
            >
              <MapContainer center={position || INDIA_CENTER} zoom={position ? 15 : 5} style={{ height: '100%', width: '100%' }}>
                <TileLayer attribution="&copy; OpenStreetMap contributors" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                <MapController position={position} onChange={setPosition} />
                {position && <>
                  <Circle center={position} radius={Number(form.geofence_radius_meters)} pathOptions={{ color: '#2f6bff', fillOpacity: 0.16 }} />
                  <Marker draggable position={position} icon={pinIcon} ref={markerRef} eventHandlers={{ dragend: () => setPosition(markerRef.current.getLatLng()) }} />
                </>}
              </MapContainer>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ flexShrink: 0, px: 3, py: 2, bgcolor: '#fff', borderTop: '1px solid #eef0f4' }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={loading} onClick={submit}>{item ? 'Update' : 'Create'}</Button>
      </DialogActions>
    </Dialog>
  );
}
